import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import colors from 'theme/colors';
import useReservationAvailability from './useReservationAvailability';
import AvailabilityCalendar from './AvailabilityCalendar';
import GuestsPicker from './GuestsPicker';
import TimeSlotPicker from './TimeSlotPicker';

const DEFAULT_PARTY_SIZE = 2;
// How many months ahead the initial "find the next available date" search will look.
const MAX_MONTHS_AHEAD = 6;

// value: { partySize, date, time } — date is 'YYYY-MM-DD', time is 'HH:mm'.
export default function ReservationPicker({ slug, value, onChange, refreshKey }) {
    const [startMonth] = useState(() => dayjs().startOf('month'));
    // The picker remounts every time the dialog reopens while the parent keeps `value`, so start
    // on the already-selected date's month instead of assuming a fresh selection.
    const [month, setMonth] = useState(() => (value.date ? dayjs(value.date).startOf('month') : startMonth));
    // True until a date is selected (automatically or by the user): while true, an empty
    // month makes the picker advance to the next one looking for the first available date.
    // It also dims the calendar, so it must never start true when a date is already selected.
    const [searching, setSearching] = useState(!value.date);
    const { days, maxPartySize, loadedKey, loading, error, reload } = useReservationAvailability(
        slug,
        month.format('YYYY-MM'),
        value.partySize,
    );

    // `days` is only trustworthy for the current month + party size once the fetch for it landed.
    const isCurrent = loadedKey === `${month.format('YYYY-MM')}|${value.partySize}`;
    const ready = isCurrent && !loading && !error;

    // Parent bumps refreshKey (e.g. after a 422 on submit) to force a refetch.
    useEffect(() => {
        if (refreshKey) reload();
    }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Once we know the max, make sure the selected party size fits.
    useEffect(() => {
        if (maxPartySize && value.partySize > maxPartySize) {
            onChange({ ...value, partySize: Math.min(DEFAULT_PARTY_SIZE, maxPartySize), date: '', time: '' });
        }
    }, [maxPartySize]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clear a selection that a refetch no longer offers.
    useEffect(() => {
        if (!ready) return;
        if (value.date && month.format('YYYY-MM') === value.date.slice(0, 7)) {
            const slots = days[value.date];
            if (!slots?.length) onChange({ ...value, date: '', time: '' });
            else if (value.time && !slots.includes(value.time)) onChange({ ...value, time: '' });
        }
    }, [days, ready]); // eslint-disable-line react-hooks/exhaustive-deps

    // Always keep a date selected: the earliest available one in the visible month (today, if
    // it's available). On the initial load, an empty month moves on to the next month.
    useEffect(() => {
        if (!ready || value.date) return;
        const earliest = Object.keys(days).filter(d => days[d]?.length).sort()[0];
        if (earliest) {
            setSearching(false);
            onChange({ ...value, date: earliest, time: '' });
        } else if (searching) {
            if (month.diff(startMonth, 'month') < MAX_MONTHS_AHEAD) setMonth(m => m.add(1, 'month'));
            else setSearching(false);
        }
    }, [ready, days, value.date, searching]); // eslint-disable-line react-hooks/exhaustive-deps

    const hasAnyDay = Object.keys(days).length > 0;
    const slots = value.date ? days[value.date] || [] : [];

    return (
        <Wrapper>
            <Section>
                <SectionTitle>Data</SectionTitle>
                <AvailabilityCalendar
                    month={month}
                    days={days}
                    loading={loading || searching}
                    selectedDate={value.date}
                    onSelectDate={date => {
                        setSearching(false);
                        onChange({ ...value, date, time: '' });
                    }}
                    onChangeMonth={delta => {
                        setSearching(false);
                        setMonth(m => m.add(delta, 'month'));
                        if (value.date || value.time) onChange({ ...value, date: '', time: '' });
                    }}
                />
                {error && (
                    <Notice role='alert'>
                        Não foi possível carregar os horários.{' '}
                        <RetryButton type='button' onClick={reload}>Tentar novamente</RetryButton>
                    </Notice>
                )}
                {!error && !loading && !searching && !hasAnyDay && (
                    <Notice>Sem horários disponíveis neste mês.</Notice>
                )}
            </Section>

            {value.date && slots.length > 0 && (
                <Section>
                    <SectionTitle>Horário</SectionTitle>
                    <TimeSlotPicker
                        slots={slots}
                        value={value.time}
                        onChange={time => onChange({ ...value, time })}
                    />
                </Section>
            )}

            <Section>
                <SectionTitle>Pessoas</SectionTitle>
                <GuestsPicker
                    value={value.partySize}
                    max={maxPartySize || Math.max(DEFAULT_PARTY_SIZE, value.partySize)}
                    onChange={partySize => onChange({ ...value, partySize })}
                />
            </Section>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SectionTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Notice = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${colors.night};
    text-align: center;
`;

const RetryButton = styled.button`
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    text-decoration: underline;
    cursor: pointer;
`;
